import {
	createRouter,
	createWebHistory,
	type RouterScrollBehavior,
} from 'vue-router'

import type { AdminRole, MemberRole } from '@/javascript/interfaces'
import { useCurriculumsStore } from '@vuejs/stores/CurriculumsStore'
import { type FeatureFlagKey, useFeatureFlagStore } from '@vuejs/stores/FeatureFlagStore'
import { useFranchisorStore } from '@vuejs/stores/FranchisorStore'
import { useGlobalStore } from '@vuejs/stores/GlobalStore'
import { useTeamStore } from '@vuejs/stores/TeamStore'
import { useTrainingLogStore } from '@vuejs/stores/TrainingLogStore'

import type { PricingTier } from '../services/courses/interfaces'
import { getAgencyId } from '../utils/getAgencyId'
import { getCurriculumParamsFromUrl } from '../utils/getCurriculumParamsFromUrl'

// Dynamic imports for better code splitting
const AgencyAdministrators = () => import('../components/administrator_list/AgencyAdministrators')
const SeatUpgradeAddSeats = () => import('../components/seatUpgrade/SeatUpgrade')
const SeatUpgradeCompleted = () => import('../components/seatUpgrade/SeatUpgradeCompleted')
const AccountInformation = () => import('../components/setup/AccountInformation')
const ServiceOffering = () => import('../components/setup/ServiceOffering')
const ToolsUsed = () => import('../components/setup/ToolsUsed')
const TrainingRoles = () => import('../components/setup/TrainingRoles')
const GroupTeamMembers = () => import('../components/team/GroupTeamMembers')
const GroupTraining = () => import('../components/team/GroupTraining')

import designSystemRoute from '../design-system/router'
import { DEFAULT_TITLE, DEFAULT_TITLE_TEAM } from '../helpers/_constants'

declare module 'vue-router' {
	interface RouteMeta {
		blockedByFeatureFlag?: FeatureFlagKey
		breadcrumb?: string
		requiresFeatureFlag?: FeatureFlagKey
		title?: string
	}
}

const AccountSuspended = () => import('../views/account-suspended')
const Catalog = () => import('../views/catalog/')
const ComplianceBundles = () => import('../views/catalog/ComplianceBundles')
const FullCatalog = () => import('../views/catalog/FullCatalog')
const Recommendations = () => import('../views/catalog/RecommendedSpecializedBundles')
const OrganizationManagement = () => import('../views/organization-management')
const Billing = () => import('../views/organization-management/Billing')
const MemberSettings = () => import('../views/organization-management/MemberSettings')
const SeatUpgrade = () => import('../views/seat-upgrade')
const AccountSetup = () => import('../views/setup')
const SignUp = () => import('../views/sign-up/')
const SignUpFlow = () => import('../views/sign-up/SignUpFlow')
const Team = () => import('../views/team/')
const AddTeamMember = () => import('../views/team/AddTeamMember')
const AddTrainingGroup = () => import('../views/team/AddTrainingGroup')
const AgencyTeam = () => import('../views/team/AgencyTeam')
const TeamMember = () => import('../views/team/TeamMember')
const TeamMemberDetails = () => import('../views/team/TeamMemberDetails')
const TeamMemberInformation = () => import('../views/team/TeamMemberInformation')
const TrainingGroup = () => import('../views/team/TrainingGroup')
const Upgrade = () => import('../views/upgrade/')
const CheckoutFlow = () => import('../views/upgrade/CheckoutFlow')
const PlanStep = () => import('../views/upgrade/PlanStep')
const CheckoutStep = () => import('../views/upgrade/CheckoutStep')
const SuccessStep = () => import('../views/upgrade/SuccessStep')
const MessageLogs = () => import('../views/message-logs/')

const scrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
	if (savedPosition) {
		return savedPosition
	} else if (to.hash) {
		const targetElement = document.querySelector(to.hash)

		if (targetElement) {
			return {
				behavior: 'smooth',
				el: to.hash,
			}
		} else {
			return { top: 0 }
		}
	} else {
		return { top: 0 }
	}
}

const router = createRouter({
	history: createWebHistory(),
	routes: [
		{
			children: [
				{
					component: AccountInformation,
					meta: { breadcrumb: 'Account setup', title: 'Account Setup: Account information' },
					name: 'AccountInformation',
					path: 'account',
				},
				{
					component: TrainingRoles,
					meta: { breadcrumb: 'Account setup', title: 'Account Setup: Training roles' },
					name: 'TrainingRoles',
					path: 'roles',
				},
				{
					component: ServiceOffering,
					meta: { breadcrumb: 'Account setup', title: 'Account Setup: Service offering' },
					name: 'ServiceOffering',
					path: 'services',
				},
				{
					component: ToolsUsed,
					meta: { breadcrumb: 'Account setup', title: 'Account Setup: Tools used' },
					name: 'ToolsUsed',
					path: 'tools',
				},
			],
			component: AccountSetup,
			path: '/setup',
			redirect: { name: 'AccountInformation' },
		},
		{
			component: AccountSuspended,
			meta: { title: 'Account Suspended' },
			name: 'account-suspended',
			path: '/agencies/:agencyId/suspended',
		},
		{
			children: [
				{
					component: ComplianceBundles,
					meta: {
						breadcrumb: 'Compliance bundles',
						requiresFeatureFlag: 'curriculum_discoverability' as const,
						title: 'Compliance bundles',
					},
					name: 'ComplianceBundles',
					path: 'compliance-bundles',
				},
				{
					component: FullCatalog,
					meta: { breadcrumb: 'Full catalog', title: 'Full catalog' },
					name: 'FullCatalog',
					path: 'classes',
				},
				{
					component: Recommendations,
					meta: { breadcrumb: 'Specialized bundles', title: 'Specialized bundles' },
					name: 'Specialized bundles',
					path: 'recommendations',
				},
			],
			component: Catalog,
			meta: { breadcrumb: 'Training catalog', title: 'Training catalog' },
			path: '/agencies/:agencyId/catalog',
			redirect: () => {
				const featureFlagStore = useFeatureFlagStore()

				if (featureFlagStore.isFeatureEnabled('curriculum_discoverability')) {
					return { name: 'ComplianceBundles' }
				}

				return { name: 'FullCatalog' }
			},
		},
		{
			children: [
				{
					component: AgencyAdministrators,
					meta: { breadcrumb: 'Administrators', title: 'Administrators' },
					name: 'organization-management-administrators',
					path: 'administrators',
				},
				{
					component: Billing,
					meta: { breadcrumb: 'Billing', title: 'Billing' },
					name: 'organization-management-billing',
					path: 'billing',
				},
				{
					component: MemberSettings,
					meta: { breadcrumb: 'Member settings', title: 'Member Settings' },
					name: 'organization-management-member-settings',
					path: 'member-settings',
				},
			],
			component: OrganizationManagement,
			meta: { breadcrumb: 'Organization management', title: 'Organization management' },
			name: 'organization-management',
			path: '/agencies/:agencyId/organization-management',
			redirect: { name: 'organization-management-administrators' },
		},
		{
			children: [
				{
					component: SeatUpgradeAddSeats,
					name: 'SeatUpgradeAddSeats',
					path: 'add-seats',
				},
				{
					component: SeatUpgradeCompleted,
					name: 'SeatUpgradeCompleted',
					path: 'completed',
				},
			],
			component: SeatUpgrade,
			meta: {
				blockedByFeatureFlag: 'seat_guardrail_removal' as const,
				breadcrumb: 'Seat upgrade',
				redirectToTrialUpgrade: false,
				title: 'Seat upgrade',
			},
			path: '/:organizationType(agencies|franchisors)/:organizationId/seat-upgrade',
			redirect: to => `/${to.params.organizationType}/${to.params.organizationId}/seat-upgrade/add-seats`,
		},
		{
			children: [
				{
					component: SignUpFlow,
					meta: { breadcrumb: 'Sign up', title: 'Sign up' },
					name: 'SignUpFlow',
					path: '',
				},
			],
			component: SignUp,
			path: '/signup',
		},
		{
			children: [
				{
					component: AddTeamMember,
					meta: {
						breadcrumb: 'Add a team member',
						primary: {
							actionClickEvent: 'add-new-team-member',
							dataTest: 'add-new-team-member',
							name: 'Submit',
							showButton: true,
						},
						showActionButtons: true,
						title: `Add a Team Member ${DEFAULT_TITLE_TEAM}`,
					},
					name: 'AddTeamMember',
					path: ':groupId/members/add',
				},
				{
					component: AddTrainingGroup,
					meta: {
						breadcrumb: 'Add training group',
						secondary: {
							actionClickEvent: 'add-new-group',
							dataTest: 'add-new-group',
							name: 'Submit',
							showButton: true,
						},
						showActionButtons: true,
						title: `Add Training Group ${DEFAULT_TITLE_TEAM}`,
					},
					name: 'AddTrainingGroup',
					path: 'add',
				},
				{
					component: AgencyTeam,
					meta: {
						primary: {
							actionClickEvent: 'add-new-training-group',
							dataTest: 'add-new-training-group',
							icon: 'SVGPlus',
							name: 'Add training group',
							showButton: true,
						},
						secondary: {
							actionClickEvent: 'add-new-team-member',
							dataTest: 'add-new-team-member',
							icon: 'SVGPlus',
							name: 'Add team member',
							showButton: true,
						},
						showActionButtons: true,
						showAgencyTeamMenu: true,
						title: `Team ${DEFAULT_TITLE_TEAM}`,
					},
					name: 'AgencyTeam',
					path: '',
				},
				{
					children: [
						{
							component: TeamMemberDetails,
							meta: {
								breadcrumb: 'Team member details',
								hideAddTrainingLink: true,
								primary: {
									actionClickEvent: 'send-member-message',
									dataTest: 'send-member-message',
									icon: 'SVGChat',
									name: 'Message',
									showButton: true,
								},
								secondary: {
									actionClickEvent: 'add-training',
									dataTest: 'add-training',
									icon: 'SVGPlus',
									name: 'Add training',
									showButton: true,
								},
								showActionButtons: true,
								showBadge: true,
								showDropdownMenu: true,
								showMemberDropdownOptions: true,
								showMemberHeading: true,
								showOnTeamMemberDetails: true,
								showTabs: true,
								showTabTeamMember: true,
								title: `Team Member Details ${DEFAULT_TITLE_TEAM}`,
							},
							name: 'TeamMemberDetails',
							path: '',
						},
						{
							component: TeamMemberInformation,
							meta: {
								breadcrumb: 'Team member information',
								hideAddTrainingLink: true,
								primary: {
									actionClickEvent: 'send-member-message',
									dataTest: 'send-member-message',
									icon: 'SVGChat',
									name: 'Message',
									showButton: true,
								},
								secondary: {
									actionClickEvent: 'add-training',
									dataTest: 'add-training',
									icon: 'SVGPlus',
									name: 'Add training',
									showButton: true,
								},
								showActionButtons: true,
								showBadge: true,
								showDropdownMenu: true,
								showMemberDropdownOptions: true,
								showMemberHeading: true,
								showOnTeamMemberInformation: true,
								showTabs: true,
								showTabTeamMember: true,
								title: `Team Member Information ${DEFAULT_TITLE_TEAM}`,
							},
							name: 'TeamMemberInformation',
							path: 'information',
						},
					],
					component: TeamMember,
					path: ':groupId/members/:memberId',
				},
				{
					children: [
						{
							component: GroupTeamMembers,
							meta: {
								breadcrumb: 'Group members',
								canEditTitle: true,
								primary: {
									actionClickEvent: 'send-group-message',
									dataTest: 'send-group-message',
									icon: 'SVGChat',
									name: 'Message',
									showButton: true,
								},
								secondary: {
									actionClickEvent: 'add-group-members',
									dataTest: 'add-group-members',
									icon: 'SVGPlus',
									name: 'Add group member',
									showButton: true,
								},
								showActionButtons: true,
								showDropdownMenu: true,
								showOnGroupMembers: true,
								showTabGroupMembers: true,
								showTabs: true,
								showTitleFlare: true,
								showTrainingCurriculumRadioGroup: true,
								showTrainingGroupMenu: true,
								title: `Group Members ${DEFAULT_TITLE_TEAM}`,
								useContainerClass: true,
							},
							name: 'GroupMembers',
							path: 'members',
						},
						{
							component: GroupTraining,
							meta: {
								breadcrumb: 'Group training',
								canEditTitle: true,
								primary: {
									actionClickEvent: 'send-group-message',
									dataTest: 'send-group-message',
									icon: 'SVGChat',
									name: 'Message',
									showButton: true,
								},
								secondary: {
									actionClickEvent: 'add-group-members',
									dataTest: 'add-group-members',
									icon: 'SVGPlus',
									name: 'Add group member',
									showButton: true,
								},
								showActionButtons: true,
								showDropdownMenu: true,
								showOnGroupTraining: true,
								showTabGroupMembers: true,
								showTabs: true,
								showTitleFlare: true,
								showTrainingCurriculumRadioGroup: true,
								showTrainingGroupMenu: true,
								title: `Group Training ${DEFAULT_TITLE_TEAM}`,
							},
							name: 'GroupTraining',
							path: 'training',
						},
					],
					component: TrainingGroup,
					path: ':groupId',
					redirect: { name: 'GroupTraining' },
				},
			],
			component: Team,
			path: '/agencies/:agencyId/team',
		},
		{
			children: [
				{
					children: [
						{
							component: PlanStep,
							name: 'UpgradePlan',
							path: 'plan',
						},
						{
							component: CheckoutStep,
							name: 'UpgradeCheckout',
							path: 'checkout',
						},
						{
							component: SuccessStep,
							name: 'UpgradeSuccess',
							path: 'success',
						},
					],
					component: CheckoutFlow,
					name: 'CheckoutFlow',
					path: '',
					redirect: { name: 'UpgradePlan' },
				},
			],
			component: Upgrade,
			meta: { breadcrumb: 'Upgrade', title: 'Upgrade' },
			path: '/agencies/:agencyId/upgrade',
		},
		{
			component: MessageLogs,
			meta: { breadcrumb: 'Message logs', title: 'Message Logs' },
			name: 'MessageLogs',
			path: '/agencies/:agencyId/message_logs',
		},
		designSystemRoute,
	],
	scrollBehavior,
})

router.onError(error => {
	if (error.message.includes('Failed to fetch dynamically imported module')) {
		console.info('New version detected. Reloading page...')
		window.location.reload()
	}
})

router.beforeEach((to, from, next) => {
	if (to.matched.some(record => record.meta.redirectToTrialUpgrade) && !to.meta?.redirectToTrialUpgrade) {
		return next({ path: `/agencies/${getAgencyId()}/upgrade` })
	}

	if (to.path === from.path && to.hash === from.hash && JSON.stringify(to.query) === JSON.stringify(from.query)) {
		return next(false)
	}

	const featureFlagStore = useFeatureFlagStore()

	const blockedRecord = to.matched.find(record => record.meta.blockedByFeatureFlag)

	if (blockedRecord && featureFlagStore.isFeatureEnabled(blockedRecord.meta.blockedByFeatureFlag as FeatureFlagKey)) {
		next(false)
		window.location.href = `/${to.params.organizationType}/${to.params.organizationId}`

		return
	}

	if (to.meta?.requiresFeatureFlag) {
		const flagName: FeatureFlagKey = to.meta.requiresFeatureFlag

		if (!featureFlagStore.flags[flagName]) {
			next({ name: 'FullCatalog' })

			return
		}

		next()
	}

	const globalStore = useGlobalStore()

	globalStore.previousRoute = from
	updateStores(to.params)
	next()
})

router.beforeResolve((to, from, next) => {
	if (to.path === from.path && to.hash === from.hash && JSON.stringify(to.query) === JSON.stringify(from.query)) {
		return next(false)
	}

	const globalStore = useGlobalStore()

	globalStore.previousRoute = from

	const previousRouteURL = from.fullPath

	window.sessionStorage.setItem('previousRoute', previousRouteURL)
	globalStore.previousRouteUrl = previousRouteURL
	updateStores(to.params)
	next()
})

function updateStores(params: Record<string, any>) {
	const globalStore = useGlobalStore()
	const agencyId = Number(params.agencyId) || globalStore.agencyId || getAgencyId()
	const curriculumsStore = useCurriculumsStore()
	const curriculumId = getCurriculumParamsFromUrl().curriculumId || curriculumsStore.curriculumId
	const franchisorId = Number(params.franchisorId) || globalStore.agency?.franchisor_id || null
	const groupId = Number(params.groupId) || null
	const memberId = Number(params.memberId) || null

	curriculumsStore.curriculumId = curriculumId
	useFranchisorStore().franchisorId = franchisorId

	globalStore.agency = {
		...globalStore.agency,
		billing_method: globalStore.agency?.billing_method !== undefined ? globalStore.agency.billing_method : '',
		franchisor: globalStore.agency?.franchisor !== undefined && globalStore.agency.franchisor !== null
			? {
				...globalStore.agency.franchisor,
				id: franchisorId,
				name: globalStore.agency.franchisor?.name ?? '',
			}
			: { id: franchisorId, name: '' },
		franchisor_id: franchisorId,
		id: globalStore.agency?.id !== undefined ? globalStore.agency.id : agencyId ?? 0,
		is_configuration: globalStore.agency?.is_configuration !== undefined ? globalStore.agency.is_configuration : false,
		name: globalStore.agency?.name !== undefined ? globalStore.agency.name : '',
		pricing_tier: globalStore.agency?.pricing_tier !== undefined ? globalStore.agency.pricing_tier : ({} as PricingTier),
		self_enrollment_enabled: globalStore.agency?.self_enrollment_enabled !== undefined ?
			globalStore.agency.self_enrollment_enabled : false,
		state: globalStore.agency?.state !== undefined ? globalStore.agency.state : '',
		status: globalStore.agency?.status !== undefined ? globalStore.agency.status : '',
		video_library_enabled: globalStore.agency?.video_library_enabled ?? false,
	}

	globalStore.currentUser = {
		...globalStore.currentUser,
		agency: {
			...globalStore.currentUser?.agency,
			billing_method: globalStore.currentUser?.agency?.billing_method ?? '',
			franchisor: globalStore.currentUser?.agency?.franchisor ?? { id: null },
			id: agencyId,
			is_configuration: globalStore.currentUser?.agency?.is_configuration ?? false,
			name: globalStore.currentUser?.agency?.name ?? '',
			pricing_tier: globalStore.currentUser?.agency?.pricing_tier ?? {} as PricingTier,
			self_enrollment_enabled: globalStore.currentUser?.agency?.self_enrollment_enabled ?? false,
			state: globalStore.currentUser?.agency?.state ?? '',
			status: globalStore.currentUser?.agency?.status ?? '',
			video_library_enabled: globalStore.currentUser?.agency?.video_library_enabled ?? false,
		},
		email: globalStore.currentUser?.email ?? '',
		first_name: globalStore.currentUser?.first_name ?? '',
		franchisor: globalStore.currentUser?.franchisor ?? { id: null },
		free_trial: globalStore.currentUser?.free_trial ?? '',
		id: globalStore.currentUser?.id ?? 0,
		isCaregiver: globalStore.currentUser?.isCaregiver ?? false,
		isFranchisorManager: globalStore.currentUser?.isFranchisorManager ?? false,
		isMoreThanCaregiver: globalStore.currentUser?.isMoreThanCaregiver ?? false,
		isSuper: globalStore.currentUser?.isSuper ?? false,
		last_name: globalStore.currentUser?.last_name ?? '',
		name: globalStore.currentUser?.name ?? '',
		phone: globalStore.currentUser?.phone ?? '',
		role: globalStore.currentUser?.role ?? '' as AdminRole | MemberRole,
		tags: globalStore.currentUser?.tags ?? [],
		trainingPath: globalStore.currentUser?.trainingPath ?? '',
	}

	const teamStore = useTeamStore()

	teamStore.selectedCurriculumId = Number(params.curriculumId) || null
	teamStore.selectedGroupId = groupId
	teamStore.selectedMemberId = memberId

	useTrainingLogStore().agencyId = agencyId
	useTrainingLogStore().franchisorId = franchisorId
}

router.afterEach(to => {
	document.title = to.meta?.title as string || DEFAULT_TITLE
})

export default router
